import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Download, ArrowLeft } from "lucide-react";
import api, { BACKEND } from "@/lib/api";

export default function AlbumView() {
  const { id } = useParams();
  const [album, setAlbum] = useState(null);
  const [idx, setIdx] = useState(-1);

  useEffect(() => { api.get(`/gallery/${id}`).then((r) => setAlbum(r.data)); }, [id]);

  const resolve = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);

  const next = useCallback(() => setIdx((i) => (album ? (i + 1) % album.photos.length : -1)), [album]);
  const prev = useCallback(() => setIdx((i) => (album ? (i - 1 + album.photos.length) % album.photos.length : -1)), [album]);

  useEffect(() => {
    if (idx < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") setIdx(-1);
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, next, prev]);

  if (!album) return <div className="p-16 text-center text-gray-500">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Link to="/gallery" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-6" data-testid="album-back">
        <ArrowLeft className="w-4 h-4" /> Back to gallery
      </Link>
      <div className="fade-up">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4F46E5] mb-2">{album.training_name}</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-semibold">{album.school_name}</h1>
        <p className="mt-2 text-gray-600">{album.date} · {album.location} · {album.teacher_count} teachers</p>
      </div>
      <div className="mt-10 columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
        {album.photos.map((p, i) => (
          <button key={i} onClick={() => setIdx(i)} data-testid={`photo-${i}`} className="mb-4 block w-full break-inside-avoid overflow-hidden rounded-xl group">
            <img src={resolve(p)} alt="" loading="lazy" className="w-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
          </button>
        ))}
      </div>

      {idx >= 0 && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={() => setIdx(-1)}>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="lightbox-prev" className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center text-white">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="lightbox-next" className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center text-white">
            <ChevronRight className="w-5 h-5" />
          </button>
          <button onClick={() => setIdx(-1)} data-testid="lightbox-close" className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center text-white">
            <X className="w-5 h-5" />
          </button>
          <a href={resolve(album.photos[idx])} download className="absolute top-4 right-16 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center text-white">
            <Download className="w-5 h-5" />
          </a>
          <img src={resolve(album.photos[idx])} alt="" onClick={(e) => e.stopPropagation()} className="max-w-[92vw] max-h-[88vh] object-contain fade-up" />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs">
            {idx + 1} / {album.photos.length}
          </div>
        </div>
      )}
    </div>
  );
}
